<template>
    <v-container :fluid="true" class="max-height pb-0">
        <v-row class="h-100 pb-0">
            <v-col class="h-100 pb-0" cols="12" md="3">
                <device-list
                    :new-device-selection="selectedDevice"
                    @loaded="updateDevices"
                    @nearestToDevice="(device)=>deviceToNearest=device"
                    @zoomToDevice="(device)=>deviceToZoom=device"
                    @select-device="deviceSelection"
                />
            </v-col>
            <v-col class="h-100 pb-0" cols="12" md="6">
                <devices-on-map
                    :devices="devices"
                    :headers="deviceHeaders"
                    :zoomToDevice="deviceToZoom"
                    @select-device="deviceSelection"
                    :nearestToDevice="deviceToNearest"
                    :selected-device="selectedDevice"
                />
            </v-col>
            <v-col class="h-100 pb-0" cols="12" md="3">
                <div class="d-flex flex-column h-100 justify-space-between">
                    <recent-trips
                        :selected-device="selectedDevice"
                        @selected-trip="(trip)=>{selectedTrip = trip}"
                    />
                    <trip-route-map
                        :selected-device="selectedDevice"
                        :selected-trip="selectedTrip"
                    />
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {onBeforeUnmount, onMounted, provide, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import pahoMqttClient from '@/plugins/mqttClient';
import {fetchMqttToken, msUntilRefresh, companyTopicPrefix} from "@/composables/mqttAuth";
import {decryptMqttPayload, clearKeyCache} from "@/composables/mqttCrypto";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import DeviceList from "@/components/cards/DeviceList.vue";
import RecentTrips from "@/components/cards/RecentTrips.vue";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import DevicesOnMap from "@/components/cards/DevicesOnMap.vue";
import {normalizeSerial, sameSerial} from '@/composables/deviceSerial';


const devices = ref([])
const deviceHeaders = ref([])
const deviceToZoom = ref(null)
const selectedTrip = ref(null)
const selectedDevice = ref(null)
const deviceToNearest = ref(null)
const activeSubscriptions = new Set()
const mqttTopicMap = new Map() // topic -> slno mapping
const payloadKeyMap = new Map() // topic -> hex key for AES-256-GCM decryption
const latestLocations = ref(new Map())
const lastMqttUpdate = ref(null)
provide('lastMqttUpdate', lastMqttUpdate)
const authStore = useAuthStore();
// c/{cid}/reload is owned app-wide by the Auth layout (useCompanyReload) so the
// current page refreshes on every route; Live keeps only its device-stream topics.
const topicPrefix = companyTopicPrefix(authStore.user?.company?.id);
let mqttRefreshTimer = null;
// Set on unmount so an in-flight connectMqtt() (suspended in an await) can't
// rebuild the connection after teardown (which would spawn a self-renewing
// zombie). mqttRetryTimer re-tries connectMqtt after a token-fetch failure.
let mqttDestroyed = false;
let mqttRetryTimer = null;
let mqttHiddenAt = 0;        // when the tab was last hidden (force-reconnect on long-hidden return)
let keyRefetchTimer = null;  // debounce for a decrypt-fail-triggered key/device refetch

const route = useRoute();
const router = useRouter();
// Deep-link target from the Notifications "Show on map" action (?asset=<id>).
// Focus that tracker once the list has loaded, then strip the query.
let focusHandled = false;

const updateDevices = (data) => {
    devices.value = data.devices
    deviceHeaders.value = data.headers
    maybeFocusFromQuery();
}

const maybeFocusFromQuery = () => {
    if (focusHandled) return;
    const assetId = Number(route.query.asset);
    if (!Number.isFinite(assetId)) return;
    const device = devices.value.find((d) => Number(d.asset_id) === assetId);
    if (!device) return;
    focusHandled = true;
    deviceSelection(device);
    router.replace({query: {}});
}

const deviceSelection = (device) => {
    selectedTrip.value = null;
    selectedDevice.value = device ?? null
}

const scheduleMqttRefresh = () => {
    if (mqttRefreshTimer) clearTimeout(mqttRefreshTimer);
    // Reconnect with a fresh token shortly before the current one expires.
    // Not `|| 1500000`: msUntilRefresh() returns 0 both for "no token" AND for
    // "expires within the 60s safety window", and 0 is falsy, so the urgent case
    // was turned into a 25 minute wait that lands well past expiry. Same shape
    // and same reasoning as useCompanyReload.
    const ms = msUntilRefresh();
    mqttRefreshTimer = setTimeout(connectMqtt, ms > 0 ? ms : 5000);
};

const connectMqtt = async () => {
    clearTimeout(mqttRetryTimer); // cancel any pending token-failure retry
    try {
        if (pahoMqttClient.client && pahoMqttClient.client.isConnected()) {
            pahoMqttClient.disconnect();
        }
    } catch (e) { /* ignore */ }

    let auth;
    try {
        auth = await fetchMqttToken();
    } catch (err) {
        console.error('MQTT: could not obtain access token', err);
        // Retry after a short (jittered) delay instead of giving up: a bare return
        // here froze the map permanently for an active watcher on a single failed
        // token fetch (initial load or a token-rollover blip), since nothing else
        // re-tries. Jitter avoids a herd if the token endpoint is down fleet-wide.
        if (!mqttDestroyed) mqttRetryTimer = setTimeout(connectMqtt, 5000 + Math.floor(Math.random() * 2000));
        return;
    }
    if (mqttDestroyed) return; // component torn down while awaiting the token

    const clientId = `client-${Math.random().toString(16).slice(2)}`;
    pahoMqttClient.connect(auth.wsUrl || process.env.VUE_APP_MQTT_URL, clientId, {
        // The JWT is presented as the MQTT username; the password is unused.
        'username': auth.token,
        'password': 'jwt',
        // Passed to the plugin so the handler is re-attached on EVERY (re)connect.
        // A reconnect builds a NEW paho Client; without this the reconnected stream
        // silently receives nothing and the live map freezes until the next full
        // reconnect (the root cause of "live location sometimes stale").
        onMessageArrived: handleMqttMessage,
        // The plugin calls this to get a FRESH token before each auto-reconnect;
        // reusing an expired JWT would loop the reconnect forever.
        refreshToken: async () => {
            try {
                return (await fetchMqttToken())?.token;
            } catch (e) {
                console.error('MQTT: token refresh before reconnect failed', e);
                return null;
            }
        },
        onSuccess: () => {
            if (mqttDestroyed) return; // torn down mid-connect: don't re-arm timers
            // Reset subscription tracking so we RE-subscribe on every (re)connect:
            // a new MQTT session holds zero server-side subscriptions, so without
            // this a reconnected client would get no device topics (frozen map).
            activeSubscriptions.clear();
            synchronizeSubscriptions();
            pahoMqttClient.subscribe('s/reload');
            scheduleMqttRefresh();
        }
    });
};

// A decrypt failure on a keyed topic means the per-company MQTT payload key
// rotated; refresh keys/devices (debounced) so encrypted updates resume instead
// of that device's dot silently freezing.
const scheduleKeyRefetch = () => {
    if (keyRefetchTimer) return; // one refetch per burst of failures
    keyRefetchTimer = setTimeout(() => {
        keyRefetchTimer = null;
        console.log('MQTT: decrypt failed (key rotated?) - refreshing keys/devices');
        clearKeyCache();
        payloadKeyMap.clear();
        window.dispatchEvent(new CustomEvent('mqtt-token-refresh'));
    }, 3000);
};

const handleMqttMessage = async (message) => {
        try {
            const topic = message.destinationName;

            if (topic === 's/reload') {
                const payload = JSON.parse(message.payloadString);
                if (payload.action === 'reload_devices') {
                    console.log('MQTT: Received s/reload - refreshing devices');
                    clearKeyCache();
                    payloadKeyMap.clear();
                    window.dispatchEvent(new CustomEvent('mqtt-token-refresh'));
                }
                return;
            }

            let payload;
            const hexKey = payloadKeyMap.get(topic);
            if (hexKey) {
                try {
                    const decrypted = await decryptMqttPayload(message.payloadBytes, hexKey);
                    payload = JSON.parse(decrypted);
                } catch {
                    // Decrypt failed on a topic we DO have a key for -> the per-company
                    // key likely rotated. Debounced refetch pulls the new key so this
                    // device's updates resume instead of freezing until the next reload.
                    scheduleKeyRefetch();
                    try {
                        payload = JSON.parse(message.payloadString);
                    } catch {
                        console.warn('MQTT: Failed to decrypt or parse message on topic:', topic);
                        return;
                    }
                }
            } else {
                payload = JSON.parse(message.payloadString);
            }

            // Normalised at the point of entry so every downstream consumer -
            // the obd lookup below, the latestLocations key, and the slno handed
            // out on lastMqttUpdate - compares against the API's trimmed form
            // rather than the gateway's padded char(16) one.
            const slno = normalizeSerial(payload.slno)
                || normalizeSerial(mqttTopicMap.get(topic))
                || normalizeSerial(topic.split('/').pop());

            if (payload.type === 'obd_update') {
                const deviceIndex = devices.value.findIndex(d => sameSerial(d.slno, slno));
                if (deviceIndex > -1) {
                    const device = devices.value[deviceIndex];
                    if (payload.obd_type === 'vin') {
                        device.acquired_vin = payload.acquired_vin;
                    } else if (payload.obd_type === 'mil') {
                        device.indication_lamp_status = payload.indication_lamp_status ? 1 : 0;
                        // The same message carries the count, and it is the exact
                        // source the API reads dtc_count from (obd_data_mils
                        // .pending_dtcs). Dropping it meant the lamp lit live while
                        // the fault count beside it kept showing whatever the last
                        // REST load said. The Go app already merges it this way.
                        if (payload.pending_dtcs !== undefined && payload.pending_dtcs !== null) {
                            device.dtc_count = Number(payload.pending_dtcs) || 0;
                        }
                    }
                }
                return;
            }

            // Guard against null/undefined/malformed timestamps that produce Invalid Date
            const newTimestamp = payload.last_message_received ? new Date(payload.last_message_received) : null;
            const prevMessage = latestLocations.value.get(slno);

            let updated = false;
            if (prevMessage) {
                const lastTimestamp = prevMessage.last_message_received ? new Date(prevMessage.last_message_received) : null;
                // Accept update if: new timestamp is valid and newer, OR previous was invalid/missing
                if (!lastTimestamp || isNaN(lastTimestamp) || (newTimestamp && !isNaN(newTimestamp) && newTimestamp > lastTimestamp)) {
                    latestLocations.value.set(slno, payload);
                    updated = true;
                }
            } else {
                latestLocations.value.set(slno, payload);
                updated = true;
            }

            if (updated) {
                lastMqttUpdate.value = { slno, data: {...payload} };
            }

        } catch (err) {
            console.error("Error handling incoming MQTT message:", err, message);
        }
};

// ===== Live-watch heartbeat =====
// While the live map is actually being watched, ping the server so it fans out
// a silent burst refresh to the circle's phones and the map shows near-live
// movement. Two cost/battery guards: only while the tab is VISIBLE and the user
// has been active (mouse/touch/key) within the last 10 minutes - a browser left
// open and abandoned must NOT keep pinging the server / waking the circle. One
// immediate ping on focus (debounced 25s), then every 30s.
const HEARTBEAT_INTERVAL_MS = 30000;
// Debounce >= the server per-device throttle (25s) so any on-focus ping that
// passes actually delivers a wake instead of being dropped server-side.
const HEARTBEAT_MIN_GAP_MS = 25000;
// 10 min idle => person walked away. Long enough that hands-off watching of a
// moving dot (following a drive) is never silently cut off, short enough that an
// abandoned tab stops pinging promptly. Server caps bound abuse regardless.
const HEARTBEAT_IDLE_MS = 10 * 60 * 1000;
let heartbeatTimer = null;
let lastHeartbeatAt = 0;
let lastActivityAt = Date.now();
// Any of these counts as "still watching" and resets the idle clock. Chosen for
// cross-device coverage: desktop -> mousemove/wheel/keydown; touch (iPad/iPhone)
// -> touchstart/touchmove/pointerdown (mousemove/wheel never fire on finger
// input). So a drive followed by panning/zooming on any platform keeps the burst
// alive without needing literal mouse *movement*.
const ACTIVITY_EVENTS = ['mousemove', 'pointerdown', 'wheel', 'touchstart', 'touchmove', 'keydown'];

const pingLive = () => {
    if (document.visibilityState !== 'visible') return;
    // Idle guard: once the user has been inactive this long, STOP pinging the
    // server entirely (kill the timer) - an abandoned tab must go quiet. Moving
    // the mouse / touching / typing restarts it (onUserActivity below).
    if (Date.now() - lastActivityAt > HEARTBEAT_IDLE_MS) {
        stopHeartbeat();
        return;
    }
    // Debounce so focus toggles / returning-from-idle can't double-fire.
    if (Date.now() - lastHeartbeatAt < HEARTBEAT_MIN_GAP_MS) return;
    lastHeartbeatAt = Date.now();
    // Best-effort; a failed ping just means a slightly less fresh map this tick.
    send('post', 'circle/live/heartbeat').catch(() => {});
};

const startHeartbeat = () => {
    stopHeartbeat();
    pingLive();
    heartbeatTimer = setInterval(pingLive, HEARTBEAT_INTERVAL_MS);
};

const stopHeartbeat = () => {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
};

const onUserActivity = () => {
    lastActivityAt = Date.now();
    // If pinging was stopped (idle timeout, or not yet started) and the tab is
    // visible, any activity restarts it right away.
    if (heartbeatTimer === null && document.visibilityState === 'visible') {
        startHeartbeat();
    }
};

// Reconnect MQTT + resume/pause the heartbeat when tab visibility changes
const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
        if (process.env.VUE_APP_MQTT_URL) {
            const hiddenMs = mqttHiddenAt ? Date.now() - mqttHiddenAt : 0;
            const notConnected = !pahoMqttClient.client || !pahoMqttClient.client.isConnected();
            // Reconnect if disconnected OR if we were hidden long enough that the
            // socket could have died SILENTLY while background timers (paho's
            // keepalive ping) were throttled - isConnected() can report true for a
            // half-dead socket, so don't trust it after a long hidden interval.
            if (notConnected || hiddenMs > 45000) {
                console.log('MQTT: Tab visible - reconnecting');
                connectMqtt();
            }
        }
        mqttHiddenAt = 0;
        // Regaining focus counts as activity so the burst resumes right away.
        lastActivityAt = Date.now();
        startHeartbeat();
    } else {
        mqttHiddenAt = Date.now();
        stopHeartbeat();
    }
};

onMounted(() => {
    if (process.env.VUE_APP_MQTT_URL) {
        connectMqtt();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    ACTIVITY_EVENTS.forEach(
        (e) => window.addEventListener(e, onUserActivity, {passive: true}));
    // Only start now if the tab is actually visible; onVisibilityChange starts it
    // when a background-opened tab is later shown (avoids an idle no-op timer).
    if (document.visibilityState === 'visible') startHeartbeat();
});

onBeforeUnmount(() => {
    mqttDestroyed = true;
    clearTimeout(mqttRetryTimer);
    clearTimeout(keyRefetchTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onUserActivity));
    stopHeartbeat();
    if (mqttRefreshTimer) clearTimeout(mqttRefreshTimer);
    if (process.env.VUE_APP_MQTT_URL) {
        pahoMqttClient.unsubscribe('s/reload');
        activeSubscriptions.forEach((topic) => {
            pahoMqttClient.unsubscribe(topic);
        });
        activeSubscriptions.clear();
        mqttTopicMap.clear();
        payloadKeyMap.clear();
        clearKeyCache();
        pahoMqttClient.disconnect();
    }
});

// Watch for changes in the devices array
watch(devices, synchronizeSubscriptions, {deep: true});

function synchronizeSubscriptions() {
    if (!pahoMqttClient.client || !pahoMqttClient.client.isConnected()) {
        console.warn('MQTT client is not connected');
        return;
    }

    // Build topics from mqtt_topic (token-based) or fallback to slno
    const currentTopics = new Set();
    mqttTopicMap.clear();
    payloadKeyMap.clear();
    devices.value.forEach((device) => {
        const topic = topicPrefix + (device.mqtt_topic || `d/${device.slno}`);
        currentTopics.add(topic);
        mqttTopicMap.set(topic, normalizeSerial(device.slno));
        if (device.mqtt_payload_key) {
            payloadKeyMap.set(topic, device.mqtt_payload_key);
        }
    });

    // Subscribe to topics for new devices
    currentTopics.forEach((topic) => {
        if (!activeSubscriptions.has(topic)) {
            pahoMqttClient.subscribe(topic);
            activeSubscriptions.add(topic);
            console.log(`Subscribed to: ${topic}`);
        }
    });

    // Unsubscribe from topics that are no longer in the devices array
    activeSubscriptions.forEach((topic) => {
        if (!currentTopics.has(topic)) {
            pahoMqttClient.unsubscribe(topic);
            activeSubscriptions.delete(topic);
            console.log(`Unsubscribed from: ${topic}`);
        }
    });
}
</script>
